import BlockedAccountGate from "@/components/BlockedAccountGate";
import PublicStyleSync from "@/components/PublicStyleSync";
import { captureLeadId } from "@/lib/leadAttribution";
captureLeadId();
import BetaalLink from "./pages/BetaalLink";
import LegacyBetaalRedirect from "./pages/LegacyBetaalRedirect";
import { lazy, Suspense, useCallback, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import SupportWidget from "@/components/SupportWidget";
import { CookieConsent } from "@/components/CookieConsent";
// Verkooppopups uitgezet (correctieronde): SubscriptionReminderPopup, WelcomePopup,
// BusinessCardPromoPopup, AffiliateInvitePopup en CvUploadNudgePopup onderbraken de onboarding.
import ProfilePhotoRequiredPopup from "@/components/ProfilePhotoRequiredPopup";
import PaymentWarningBanner from "@/components/PaymentWarningBanner";
import { PaymentMethodNudgeBanner } from "@/components/PaymentMethodNudgeBanner";
import PageViewTracker from "@/components/PageViewTracker";
import TrackingScripts from "@/components/TrackingScripts";
import UtmCapture from "@/components/UtmCapture";

import { AuthProvider } from "@/contexts/AuthContext";
import { TwoFactorGuard } from "@/components/TwoFactorGuard";

// Eager load the homepage for best FCP/LCP
import Index from "./pages/Index";

// Lazy load all other pages for code splitting
const Login = lazy(() => import("./pages/Login"));
const LeadUnsubscribe = lazy(() => import("./pages/LeadUnsubscribe"));
const LeadShortLink = lazy(() => import("./pages/LeadShortLink"));
const AuthBedrijf = lazy(() => import("./pages/AuthBedrijf"));
const BetalingSucces = lazy(() => import("./pages/BetalingSucces"));
const BetalingVerwerken = lazy(() => import("./pages/BetalingVerwerken"));
const BetalingGeannuleerd = lazy(() => import("./pages/BetalingGeannuleerd"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminProfiel = lazy(() => import("./pages/AdminProfiel"));
const AdminB2BNieuw = lazy(() => import("./pages/AdminB2BNieuw"));
const AdminTestProfitMetrics = lazy(() => import("./pages/AdminTestProfitMetrics"));
const TeamProfiel = lazy(() => import("./pages/TeamProfiel"));
const Zorgbureaus = lazy(() => import("./pages/Zorgbureaus"));
const ZorgbedrijvenOverzicht = lazy(() => import("./pages/ZorgbedrijvenOverzicht"));
const BedrijfDetail = lazy(() => import("./pages/BedrijfDetail"));
const ZoekZorgverlener = lazy(() => import("./pages/ZoekZorgverlener"));
const ZorgverlenerProfiel = lazy(() => import("./pages/ZorgverlenerProfiel"));
const Vacatures = lazy(() => import("./pages/Vacatures"));
const InterimVacatures = lazy(() => import("./pages/InterimVacatures"));
const Abonnementen = lazy(() => import("./pages/Abonnementen"));
const AbonnementenZorgverleners = lazy(() => import("./pages/AbonnementenZorgverleners"));
const AbonnementenClienten = lazy(() => import("./pages/AbonnementenClienten"));
const AbonnementenBedrijven = lazy(() => import("./pages/AbonnementenBedrijven"));
const PlaatsAdvertentie = lazy(() => import("./pages/PlaatsAdvertentie"));
const BewerkHulpvraag = lazy(() => import("./pages/BewerkHulpvraag"));
const MijnProfiel = lazy(() => import("./pages/MijnProfiel"));
const ClientDashboard = lazy(() => import("./pages/ClientDashboard"));
const ClientProfielSetup = lazy(() => import("./pages/ClientProfielSetup"));
const B2BDashboard = lazy(() => import("./pages/B2BDashboard"));
const BedrijfBewerken = lazy(() => import("./pages/BedrijfBewerken"));
const B2BRegistratie = lazy(() => import("./pages/B2BRegistratie"));
const HoeWerktHet = lazy(() => import("./pages/HoeWerktHet"));
const ZorgFactuur = lazy(() => import("./pages/ZorgFactuur"));
const FacturatieModule = lazy(() => import("./pages/FacturatieModule"));
const NieuweFactuur = lazy(() => import("./pages/NieuweFactuur"));
const ContentPage = lazy(() => import("./pages/ContentPage"));
const VoorZorgverleners = lazy(() => import("./pages/VoorZorgverleners"));
const VoorClienten = lazy(() => import("./pages/VoorClienten"));
const Instellingen = lazy(() => import("./pages/Instellingen"));
const Berichten = lazy(() => import("./pages/Berichten"));
const MijnNetwerk = lazy(() => import("./pages/MijnNetwerk"));
const AanbevelingGoedkeuren = lazy(() => import("./pages/AanbevelingGoedkeuren"));
const AanbevelingSchrijven = lazy(() => import("./pages/AanbevelingSchrijven"));
const LinkedInCallback = lazy(() => import("./pages/LinkedInCallback"));
const AffiliateProgram = lazy(() => import("./pages/AffiliateProgram"));
const Uitnodiging = lazy(() => import("./pages/Uitnodiging"));
const AffiliateVoorwaarden = lazy(() => import("./pages/AffiliateVoorwaarden"));
const NodigUit = lazy(() => import("./pages/NodigUit"));
const TwoFactorVerify = lazy(() => import("./pages/TwoFactorVerify"));
const TwoFactorSetupRequired = lazy(() => import("./pages/TwoFactorSetupRequired"));
const Aanbevelingen = lazy(() => import("./pages/Aanbevelingen"));
const Samenwerkingen = lazy(() => import("./pages/Samenwerkingen"));
const OverOns = lazy(() => import("./pages/OverOns"));
const KiesAbonnement = lazy(() => import("./pages/KiesAbonnement"));
const MijnPraktijkOnboarding = lazy(() => import("./pages/MijnPraktijkOnboarding"));
const HulpvraagDetail = lazy(() => import("./pages/HulpvraagDetail"));
const UserTypeSelection = lazy(() => import("./pages/UserTypeSelection"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const EmailBevestigen = lazy(() => import("./pages/EmailBevestigen"));
const EmailWijzigen = lazy(() => import("./pages/EmailWijzigen"));
const Contact = lazy(() => import("./pages/Contact"));
const GemeenteSamenwerking = lazy(() => import("./pages/GemeenteSamenwerking"));
const NieuwJaarZorg = lazy(() => import("./pages/NieuwJaarZorg"));
const CountryLanding = lazy(() => import("./pages/CountryLanding"));
const ProvinceLanding = lazy(() => import("./pages/ProvinceLanding"));
const CityLanding = lazy(() => import("./pages/CityLanding"));
const PGBZorgverlenersVinden = lazy(() => import("./pages/PGBZorgverlenersVinden"));
const PGBAanvragen = lazy(() => import("./pages/PGBAanvragen"));
const MijnPGB = lazy(() => import("./pages/MijnPGB"));
const Zorgsoorten = lazy(() => import("./pages/Zorgsoorten"));
const ZorgsoortDetail = lazy(() => import("./pages/ZorgsoortDetail"));
const Nieuws = lazy(() => import("./pages/Nieuws"));
const NieuwsArtikel = lazy(() => import("./pages/NieuwsArtikel"));
const CloakedRedirect = lazy(() => import("./pages/CloakedRedirect"));
const SalesDashboard = lazy(() => import("./pages/SalesDashboard"));
const MijnAccount = lazy(() => import("./pages/MijnAccount"));
const Visitekaartjes = lazy(() => import("./pages/Visitekaartjes"));
const Community = lazy(() => import("./pages/Community"));
const AbonnementOpzeggen = lazy(() => import("./pages/AbonnementOpzeggen"));
const CaregiverDashboard = lazy(() => import("./pages/CaregiverDashboard"));
const Abonnement = lazy(() => import("./pages/Abonnement"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,        // 5 min: data is "fresh" for 5 min, no refetch
      gcTime: 30 * 60 * 1000,          // 30 min: keep cache around for back-nav
      refetchOnWindowFocus: false,     // do not refetch when tab regains focus
      refetchOnReconnect: false,       // do not refetch on every network reconnect
      retry: 1,                        // 1 retry instead of default 3
    },
  },
});

// Minimal loading fallback - just empty div to avoid layout shift
const PageLoader = () => <div className="min-h-screen" />;

const PaymentBanners = () => {
  const [warningVisible, setWarningVisible] = useState(false);
  const [warningReady, setWarningReady] = useState(false);
  const onWarningVisibilityChange = useCallback((visible: boolean, ready: boolean) => {
    setWarningVisible(visible);
    setWarningReady(ready);
  }, []);
  return <>
    <PaymentWarningBanner onVisibilityChange={onWarningVisibilityChange} />
    <PaymentMethodNudgeBanner warningVisible={warningVisible} warningReady={warningReady} />
  </>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <TwoFactorGuard>
            
            <PaymentBanners />
            <UtmCapture />
            <PageViewTracker />
            <TrackingScripts />
            
            <Suspense fallback={<PageLoader />}>
            <PublicStyleSync />
            <BlockedAccountGate />
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Login />} />
              <Route path="/auth/bedrijf" element={<AuthBedrijf />} />
              {/* Backwards-compat: keep /login working for the many internal redirects, render same Login component */}
              <Route path="/login" element={<Login />} />
              <Route path="/leads/afmelden" element={<LeadUnsubscribe />} />
              <Route path="/l/:code" element={<LeadShortLink />} />
              <Route path="/registreer" element={<Navigate to="/auth?tab=register" replace />} />
              <Route path="/2fa-verify" element={<TwoFactorVerify />} />
              <Route path="/2fa-setup-required" element={<TwoFactorSetupRequired />} />
              <Route path="/betaling-succes" element={<BetalingSucces />} />
              <Route path="/betaling-verwerken" element={<BetalingVerwerken />} />
              <Route path="/betaling-geannuleerd" element={<BetalingGeannuleerd />} />
              <Route path="/zzadmin" element={<AdminDashboard />} />
              <Route path="/zzadmin/b2b/nieuw" element={<AdminB2BNieuw />} />
              <Route path="/zzadmin/:category" element={<AdminDashboard />} />
              <Route path="/zzadmin-profiel" element={<AdminProfiel />} />
              <Route path="/admin/test-profitmetrics" element={<AdminTestProfitMetrics />} />
              <Route path="/team" element={<TeamProfiel />} />
              <Route path="/zorgbedrijven" element={<Zorgbureaus />} />
              <Route path="/zorgbureaus" element={<Navigate to="/zorgbedrijven" replace />} />
              <Route path="/zorgbedrijven-overzicht" element={<ZorgbedrijvenOverzicht />} />
              <Route path="/bedrijven" element={<ZorgbedrijvenOverzicht />} />
              <Route path="/bedrijfspagina-bewerken" element={<BedrijfBewerken />} />
              <Route path="/zzadmin/bedrijf/:userId" element={<BedrijfBewerken />} />
              <Route path="/bedrijven/:slug" element={<BedrijfDetail />} />
              <Route path="/zoek-zorgverlener" element={<ZoekZorgverlener />} />
              <Route path="/zorgverlener/:id" element={<ZorgverlenerProfiel />} />
              <Route path="/vacatures" element={<Vacatures />} />
              <Route path="/interim-vacatures" element={<InterimVacatures />} />
              <Route path="/abonnementen" element={<Abonnementen />} />
              <Route path="/abonnementen/zorgverleners" element={<AbonnementenZorgverleners />} />
              <Route path="/abonnementen/clienten" element={<AbonnementenClienten />} />
              <Route path="/abonnementen/bedrijven" element={<AbonnementenBedrijven />} />
              <Route path="/abonnementen-zorgverleners" element={<Navigate to="/abonnementen/zorgverleners" replace />} />
              <Route path="/abonnementen-clienten" element={<Navigate to="/abonnementen/clienten" replace />} />
              <Route path="/abonnementen-bedrijven" element={<Navigate to="/abonnementen/bedrijven" replace />} />
              <Route path="/hoe-werkt-het" element={<HoeWerktHet />} />
              <Route path="/kies-abonnement" element={<KiesAbonnement />} />
              {/* Eén onboarding-traject voor zorgverleners; oude ingangen wijzen hierheen. */}
              <Route path="/mijn-praktijk/onboarding" element={<MijnPraktijkOnboarding />} />
              <Route path="/zo-zien-zorgvragers-je" element={<Navigate to="/mijn-praktijk/onboarding" replace />} />
              <Route path="/welkom" element={<Navigate to="/mijn-praktijk/onboarding" replace />} />
              <Route path="/profiel-opbouwen" element={<Navigate to="/mijn-praktijk/onboarding" replace />} />
              <Route path="/client-dashboard" element={<ClientDashboard />} />
              <Route path="/client-profiel-setup" element={<ClientProfielSetup />} />
              <Route path="/client-account" element={<MijnAccount />} />
              <Route path="/voor-clienten" element={<VoorClienten />} />
              <Route path="/pgb-zorgverleners-vinden" element={<PGBZorgverlenersVinden />} />
              <Route path="/pgb-aanvragen" element={<PGBAanvragen />} />
              <Route path="/mijn-pgb" element={<MijnPGB />} />
              <Route path="/sales-dashboard" element={<SalesDashboard />} />
              <Route path="/plaats-advertentie" element={<PlaatsAdvertentie />} />
              <Route path="/bewerk-hulpvraag/:id" element={<BewerkHulpvraag />} />
              {/* Community pages */}
              <Route path="/community" element={<Community />} />
              <Route path="/community/visitekaartjes" element={<Visitekaartjes />} />
              {/* Caregiver nested routes under /mijn-praktijk */}
              <Route path="/mijn-praktijk" element={<CaregiverDashboard />} />
              <Route path="/mijn-praktijk/start" element={<Navigate to="/mijn-praktijk/onboarding" replace />} />
              <Route path="/mijn-praktijk/profiel" element={<MijnProfiel />} />
              <Route path="/mijn-praktijk/facturatie" element={<FacturatieModule />} />
              <Route path="/mijn-praktijk/facturatie/nieuw" element={<NieuweFactuur />} />
              <Route path="/mijn-praktijk/instellingen" element={<Instellingen />} />
              <Route path="/mijn-praktijk/instellingen/opzeggen" element={<AbonnementOpzeggen />} />
              <Route path="/mijn-praktijk/abonnement" element={<Abonnement />} />
              <Route path="/mijn-praktijk/ambassadeur" element={<AffiliateProgram />} />
              <Route path="/mijn-praktijk/nodig-uit" element={<NodigUit />} />
              <Route path="/mijn-praktijk/aanbevelingen" element={<Aanbevelingen />} />
              <Route path="/mijn-praktijk/berichten" element={<Berichten />} />
              <Route path="/mijn-praktijk/netwerk" element={<MijnNetwerk />} />
              {/* Legacy redirects */}
              <Route path="/mijn-profiel" element={<Navigate to="/mijn-praktijk/profiel" replace />} />
              {/* Eén betaalroute: /betalen/:token. Oude /betaal/:code-links leiden ernaartoe. */}
              <Route path="/betalen/:code" element={<BetaalLink />} />
              <Route path="/betaal/:code" element={<LegacyBetaalRedirect />} />
              <Route path="/facturatie" element={<Navigate to="/mijn-praktijk/facturatie" replace />} />
              <Route path="/instellingen" element={<Navigate to="/mijn-praktijk/instellingen" replace />} />
              <Route path="/instellingen/opzeggen" element={<Navigate to="/mijn-praktijk/instellingen/opzeggen" replace />} />
              <Route path="/affiliate" element={<Navigate to="/mijn-praktijk/ambassadeur" replace />} />
              <Route path="/nodig-uit" element={<Navigate to="/mijn-praktijk/nodig-uit" replace />} />
              <Route path="/aanbevelingen" element={<Navigate to="/mijn-praktijk/aanbevelingen" replace />} />
              <Route path="/berichten" element={<Navigate to="/mijn-praktijk/berichten" replace />} />
              <Route path="/dashboard" element={<Navigate to="/mijn-praktijk" replace />} />
              <Route path="/visitekaartjes" element={<Navigate to="/community/visitekaartjes" replace />} />
              <Route path="/hulpvraag/:city/:slug" element={<HulpvraagDetail />} />
              {/* Country and Province landing pages */}
              <Route path="/nederland" element={<CountryLanding />} />
              <Route path="/belgie" element={<CountryLanding />} />
              <Route path="/duitsland" element={<CountryLanding />} />
              <Route path="/:country/:province" element={<ProvinceLanding />} />
              <Route path="/:country/:province/:city" element={<CityLanding />} />
              {/* Content pages from database */}
              <Route path="/voorwaarden" element={<ContentPage />} />
              <Route path="/disclaimer" element={<ContentPage />} />
              <Route path="/veiligheid" element={<ContentPage />} />
              <Route path="/privacy" element={<ContentPage />} />
              <Route path="/page/:slug" element={<ContentPage />} />
              {/* News + Contact + lead-gen redirect */}
              <Route path="/nieuws" element={<Nieuws />} />
              <Route path="/nieuws/:slug" element={<NieuwsArtikel />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/gratis-hulpvraag-plaatsen" element={<Navigate to="/plaats-advertentie" replace />} />
              {/* DB-menu redirects */}
              <Route path="/zorgverleners" element={<Navigate to="/zoek-zorgverlener" replace />} />
              <Route path="/prijzen" element={<Navigate to="/abonnementen" replace />} />
              {/* Restored original routes */}
              <Route path="/voor-zorgverleners" element={<VoorZorgverleners />} />
              <Route path="/over-ons" element={<OverOns />} />
              <Route path="/zorg-factuur" element={<ZorgFactuur />} />
              <Route path="/zorgsoorten" element={<Zorgsoorten />} />
              <Route path="/zorgsoorten/:slug" element={<ZorgsoortDetail />} />
              <Route path="/b2b-registratie" element={<B2BRegistratie />} />
              <Route path="/b2b-dashboard" element={<B2BDashboard />} />
              <Route path="/affiliate-voorwaarden" element={<AffiliateVoorwaarden />} />
              <Route path="/gemeente-samenwerking" element={<GemeenteSamenwerking />} />
              <Route path="/nieuw-jaar-zorg" element={<NieuwJaarZorg />} />
              <Route path="/uitnodiging" element={<Uitnodiging />} />
              <Route path="/invite/:code" element={<Uitnodiging />} />
              <Route path="/aanbeveling-goedkeuren/:token" element={<AanbevelingGoedkeuren />} />
              <Route path="/aanbeveling/:slug" element={<AanbevelingSchrijven />} />
              <Route path="/linkedin-callback" element={<LinkedInCallback />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/email-bevestigen" element={<EmailBevestigen />} />
              <Route path="/email-wijzigen" element={<EmailWijzigen />} />
              <Route path="/user-type-selection" element={<UserTypeSelection />} />
              <Route path="/kies-accounttype" element={<UserTypeSelection />} />
              <Route path="/mijn-account" element={<MijnAccount />} />
              <Route path="/samenwerkingen" element={<Samenwerkingen />} />
              <Route path="/cloaked/:slug" element={<CloakedRedirect />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
            <SupportWidget />
            <CookieConsent />
            <ProfilePhotoRequiredPopup />
          </TwoFactorGuard>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
